// src/pages/ELearning/AchievementsPage.tsx
import React, { useEffect, useState } from 'react';
import { getGamificationAchievements, getGamificationBadges, getGamificationMe } from '../../api/client';
import { Loader2, Award, Trophy, Lock, Star } from 'lucide-react';

const RARITY_COLORS: Record<string, string> = {
  common: '#94a3b8',
  rare: '#3b82f6',
  epic: '#8b5cf6',
  legendary: '#f59e0b',
};

const AchievementsPage: React.FC = () => {
  const [me, setMe] = useState<any>(null);
  const [achievements, setAchievements] = useState<any>(null);
  const [badges, setBadges] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'achievements' | 'badges'>('achievements');

  useEffect(() => {
    (async () => {
      try {
        const [m, a, b] = await Promise.all([
          getGamificationMe().catch(() => null),
          getGamificationAchievements().catch(() => null),
          getGamificationBadges().catch(() => null),
        ]);
        setMe(m);
        setAchievements(a);
        setBadges(b);
      } catch (e: any) {
        setError(e.response?.data?.error || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Loader2 size={40} className="spin" color="#2575fc" />
      </div>
    </>
  );

  if (error) return (
    <>
      <div style={{ padding: 20, background: 'rgba(231,76,60,0.1)', borderRadius: 12, color: '#e74c3c' }}>{error}</div>
    </>
  );

  const stats = achievements?.stats || {};
  const achievementsList = achievements?.items || [];
  const badgesList = badges?.items || [];
  const totalPoints = me?.totalPoints || 0;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Trophy size={32} color="#f59e0b" />
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, color: '#1a1a2e' }}>Мои достижения</h1>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={kpiCard}>
          <Star size={24} color="#f59e0b" />
          <div style={kpiValue}>{totalPoints}</div>
          <div style={kpiLabel}>Очков набрано</div>
        </div>
        <div style={kpiCard}>
          <Award size={24} color="#10b981" />
          <div style={kpiValue}>{stats.unlockedCount || 0} / {stats.totalCount || 0}</div>
          <div style={kpiLabel}>Достижений</div>
        </div>
        <div style={kpiCard}>
          <Trophy size={24} color="#8b5cf6" />
          <div style={kpiValue}>{badgesList.filter((b: any) => b.earned).length} / {badgesList.length}</div>
          <div style={kpiLabel}>Значков</div>
        </div>
      </div>

      {/* Табы */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setTab('achievements')}
          style={{ padding: '10px 20px', background: tab === 'achievements' ? '#2575fc' : 'rgba(255,255,255,0.7)', color: tab === 'achievements' ? '#fff' : '#666', border: 'none', borderRadius: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          🏅 Достижения
        </button>
        <button
          onClick={() => setTab('badges')}
          style={{ padding: '10px 20px', background: tab === 'badges' ? '#2575fc' : 'rgba(255,255,255,0.7)', color: tab === 'badges' ? '#fff' : '#666', border: 'none', borderRadius: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          🏆 Значки
        </button>
      </div>

      {tab === 'achievements' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {achievementsList.map((a: any) => (
            <div key={a._id} style={{ ...card, opacity: a.unlocked ? 1 : 0.6 }}>
              <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 8 }}>{a.unlocked ? (a.icon || '🏅') : '🔒'}</div>
              <div style={{ fontSize: 16, fontWeight: 700, textAlign: 'center', marginBottom: 4 }}>{a.title}</div>
              <div style={{ fontSize: 12, color: '#888', textAlign: 'center', marginBottom: 8 }}>{a.description}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ background: 'rgba(37,117,252,0.1)', color: '#2575fc', padding: '2px 8px', borderRadius: 8, fontWeight: 600 }}>
                  +{a.points} очков
                </span>
              </div>
              {!a.unlocked && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                  <Lock size={12} style={{ display: 'inline', marginRight: 4 }} />
                  {a.condition?.type === 'courses_completed' && `Пройди ${a.condition.value} тестов`}
                  {a.condition?.type === 'points_reached' && `Набери ${a.condition.value} очков`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'badges' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
          {badgesList.map((b: any) => {
            const color = RARITY_COLORS[b.rarity] || '#94a3b8';
            return (
              <div key={b._id} style={{ ...card, textAlign: 'center', opacity: b.earned ? 1 : 0.5, border: b.earned ? `2px solid ${color}` : '1px solid rgba(255,255,255,0.5)' }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>{b.earned ? (b.icon || '🏆') : '🔒'}</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{b.name}</div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>{b.description}</div>
                <div style={{ fontSize: 10, color, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>{b.rarity || 'common'}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const card: React.CSSProperties = { background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' };
const kpiCard: React.CSSProperties = { ...card, textAlign: 'center' };
const kpiValue: React.CSSProperties = { fontSize: 28, fontWeight: 700, margin: '8px 0', color: '#1a1a2e' };
const kpiLabel: React.CSSProperties = { fontSize: 12, color: '#666', fontWeight: 600 };

export default AchievementsPage;
